import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getSummary,
  generateSummary,
  transcribeAndSummarize,
} from "../api/summary";
import { SummaryStatus } from "../types/summary/Summary";
import type { TranscribeAndSummarizeResponse } from "../types/summary/Summary";
import { useTranscriptionPolling } from "./useJobs";
import { JobState } from "../types/job/Job";
import { useEffect, useRef, useState } from "react";

export function useGetSummary(
  customerId: string | undefined,
  interactionId: string | undefined,
  enabled: boolean = true,
) {
  return useQuery({
    queryKey: ["summary", customerId, interactionId],
    queryFn: () => getSummary(customerId!, interactionId!),
    enabled: !!customerId && !!interactionId && enabled,
    refetchOnWindowFocus: false,
    retry: false,
    staleTime: 30_000,
  });
}

export function useSummaryPolling(
  customerId: string | undefined,
  interactionId: string | undefined,
  enabled: boolean = false,
) {
  return useQuery({
    queryKey: ["summary-polling", customerId, interactionId],
    queryFn: () => getSummary(customerId!, interactionId!),
    enabled: !!customerId && !!interactionId && enabled,
    refetchInterval: (query) => {
      const summary = query.state.data;
      if (
        summary &&
        [SummaryStatus.Completed, SummaryStatus.Failed].includes(summary.status)
      ) {
        return false;
      }
      return 2000;
    },
    refetchOnWindowFocus: false,
  });
}

export function useGenerateSummary() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["generateSummary"],
    mutationFn: ({
      customerId,
      interactionId,
      templateId,
    }: {
      customerId: string;
      interactionId: string;
      templateId?: string;
    }) => generateSummary(customerId, interactionId, templateId),
    onSettled: (_, __, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["summary", variables.customerId, variables.interactionId],
      });
    },
  });
}

export function useTranscribeAndSummarize() {
  return useMutation<
    TranscribeAndSummarizeResponse,
    Error,
    {
      customerId: string;
      interactionId: string;
      templateId?: string;
    }
  >({
    mutationKey: ["transcribeAndSummarize"],
    mutationFn: ({
      customerId,
      interactionId,
      templateId,
    }) => transcribeAndSummarize(customerId, interactionId, templateId),
  });
}

export type TranscribeAndSummarizePhase =
  | "idle"
  | "transcribing"
  | "summarizing"
  | "completed"
  | "error";

export function useTranscribeAndSummarizeFlow(
  customerId: string | undefined,
  interactionId: string | undefined,
) {
  const [phase, setPhase] = useState<TranscribeAndSummarizePhase>("idle");
  const [transcriptionJobId, setTranscriptionJobId] = useState<string>();
  const queryClient = useQueryClient();
  const templateIdRef = useRef<string | undefined>(undefined);

  const transcribeAndSummarizeMutation = useTranscribeAndSummarize();
  const generateSummaryMutation = useGenerateSummary();

  const { data: jobState } = useTranscriptionPolling(
    customerId,
    phase === "transcribing" ? transcriptionJobId : undefined,
  );

  const { data: summary } = useSummaryPolling(
    customerId,
    interactionId,
    phase === "summarizing",
  );

  useEffect(() => {
    if (phase === "transcribing" && jobState === JobState.Complete) {
      setPhase("summarizing");
      generateSummaryMutation.mutate({
        customerId: customerId!,
        interactionId: interactionId!,
        templateId: templateIdRef.current,
      });
    } else if (phase === "transcribing" && jobState === JobState.Error) {
      setPhase("error");
    }
  }, [phase, jobState, customerId, interactionId, generateSummaryMutation]);

  useEffect(() => {
    if (phase === "summarizing" && summary) {
      if (summary.status === SummaryStatus.Completed) {
        setPhase("completed");
        queryClient.invalidateQueries({
          queryKey: ["summary", customerId, interactionId],
        });
      } else if (summary.status === SummaryStatus.Failed) {
        setPhase("error");
      }
    }
  }, [phase, summary, customerId, interactionId, queryClient]);

  const start = (templateId?: string) => {
    templateIdRef.current = templateId;
    setPhase("transcribing");
    transcribeAndSummarizeMutation.mutate(
      {
        customerId: customerId!,
        interactionId: interactionId!,
      },
      {
        onSuccess: (data) => {
          setTranscriptionJobId(data.jobId);
        },
        onError: () => {
          setPhase("error");
        },
      },
    );
  };

  const reset = () => {
    setPhase("idle");
    setTranscriptionJobId(undefined);
    templateIdRef.current = undefined;
  };

  return { phase, error: transcribeAndSummarizeMutation.error, start, reset };
}
